import { useWatch } from 'react-hook-form';
import { ensureHttpsProtocol } from 'v1/helpers/urlHelpers';
import { FormField } from 'v5/design-system/Form';
import { Input } from 'v5/design-system/Input';
import { InputOverlay } from 'v5/design-system/InputOverlay';
import { useFieldContext } from '../FieldContext';
import { EmptyField } from './EmptyField';
import { FieldControlBase } from './FieldControlBase';
import type { FieldComponentProps } from './fieldComponent.types';

/**
 * URL custom field (legacy `URL.jsx` / `UrlInput`): a `type="url"` input in
 * edit mode; in display mode the value is overlaid as an external link.
 */
export function UrlField({
  spec,
  path,
  hideLabel
}: FieldComponentProps<'url'>) {
  const ctx = useFieldContext();
  const readOnly = ctx.mode === 'display';
  const url: string = useWatch({ name: path }) ?? '';

  if (readOnly && url === '') {
    return <EmptyField spec={spec} path={path} hideLabel={hideLabel} />;
  }

  return (
    <FormField
      name={path}
      render={({ field }) => {
        const value: string = field.value ?? '';
        const href = ensureHttpsProtocol(value);
        const input = (
          <Input
            type="url"
            inputMode="url"
            autoComplete="off"
            readOnly={readOnly}
            variant={ctx.variant}
            size={ctx.size}
            placeholder={spec.placeholder ?? undefined}
            {...field}
            value={value}
          />
        );
        return (
          <FieldControlBase spec={spec} hideLabel={hideLabel}>
            {readOnly && value !== '' ? (
              <InputOverlay
                label={value}
                // Only web links: a stored `javascript:` value is no href.
                action={/^https?:\/\//i.test(href) ? href : null}
                size={ctx.size}
                variant={ctx.variant}
                revealOnFocus={false}
              >
                {input}
              </InputOverlay>
            ) : (
              input
            )}
          </FieldControlBase>
        );
      }}
    />
  );
}
